import { getP } from 'redux-polyglot'
import api from '../libs/api'
import { getTopCategories } from '../selectors/video-library'

/**
 * Actions for initializing the application
 */
export const INIT_APP_REQUEST = 'VIDEO_LIBRARY/INIT_APP_REQUEST'

const initAppRequest = () => ({
  type: INIT_APP_REQUEST,
})

export const initApp = () => (dispatch, getState) => {
  dispatch(initAppRequest())
  return Promise.all([
    dispatch(loadPopularVideos()),
    dispatch(loadRecentVideos()),
    dispatch(loadTopCategories()).then(() => {
      const topCategories = getTopCategories(getState())
      let moreVideosCount = 0
      topCategories.some(category => {
        dispatch(loadMoreVideos(category.id))
        moreVideosCount++
        return moreVideosCount > 9
      })
    }),
  ])
}

/**
 * Actions for dismissing an error
 */
export const DISMISS_ERROR = 'VIDEO_LIBRARY/DISMISS_ERROR'

export const dismissError = () => ({
  type: DISMISS_ERROR,
})

/**
 * Actions for fetching popular videos from API
 */
export const FETCH_POPULAR_VIDEOS_REQUEST = 'VIDEO_LIBRARY/FETCH/POPULAR_VIDEOS/REQUEST'
export const FETCH_POPULAR_VIDEOS_SUCCESS = 'VIDEO_LIBRARY/FETCH/POPULAR_VIDEOS/SUCCESS'
export const FETCH_POPULAR_VIDEOS_FAILURE = 'VIDEO_LIBRARY/FETCH/POPULAR_VIDEOS/FAILURE'

const fetchPopularVideosRequest = () => ({
  type: FETCH_POPULAR_VIDEOS_REQUEST,
})

const fetchPopularVideosSuccess = payload => ({
  type: FETCH_POPULAR_VIDEOS_SUCCESS,
  payload,
})

const fetchPopularVideosFailure = error => ({
  type: FETCH_POPULAR_VIDEOS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadPopularVideos = () => (dispatch, getState) => {
  dispatch(fetchPopularVideosRequest())
  return api
    .getPopularVideos()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const popularVideos = []
          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            popularVideos.push(processed)
          })
          return dispatch(fetchPopularVideosSuccess(popularVideos))
        } else {
          return dispatch(fetchPopularVideosFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchPopularVideosFailure(
            getP(getState()).t('tv_video_library.top_level_categories_loading_error', {
              _: 'An error occurred while loading the list of top level video categories',
            })
          )
        )
    )
}

/**
 * Actions for fetching recent videos from API
 */
export const FETCH_RECENT_VIDEOS_REQUEST = 'VIDEO_LIBRARY/FETCH/RECENT_VIDEOS/REQUEST'
export const FETCH_RECENT_VIDEOS_SUCCESS = 'VIDEO_LIBRARY/FETCH/RECENT_VIDEOS/SUCCESS'
export const FETCH_RECENT_VIDEOS_FAILURE = 'VIDEO_LIBRARY/FETCH/RECENT_VIDEOS/FAILURE'

const fetchRecentVideosRequest = () => ({
  type: FETCH_RECENT_VIDEOS_REQUEST,
})

const fetchRecentVideosSuccess = payload => ({
  type: FETCH_RECENT_VIDEOS_SUCCESS,
  payload,
})

const fetchRecentVideosFailure = error => ({
  type: FETCH_RECENT_VIDEOS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadRecentVideos = () => (dispatch, getState) => {
  dispatch(fetchRecentVideosRequest())
  return api
    .getRecentVideos()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const recentVideos = []
          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            recentVideos.push(processed)
          })
          return dispatch(fetchRecentVideosSuccess(recentVideos))
        } else {
          return dispatch(fetchRecentVideosFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchRecentVideosFailure(
            getP(getState()).t('tv_video_library.top_level_categories_loading_error', {
              _: 'An error occurred while loading the list of top level video categories',
            })
          )
        )
    )
}

/**
 * Actions for fetching top level categories from API
 */
export const FETCH_TOP_CATEGORIES_REQUEST = 'VIDEO_LIBRARY/FETCH/TOP_CATEGORIES/REQUEST'
export const FETCH_TOP_CATEGORIES_SUCCESS = 'VIDEO_LIBRARY/FETCH/TOP_CATEGORIES/SUCCESS'
export const FETCH_TOP_CATEGORIES_FAILURE = 'VIDEO_LIBRARY/FETCH/TOP_CATEGORIES/FAILURE'

const fetchTopCategoriesRequest = () => ({
  type: FETCH_TOP_CATEGORIES_REQUEST,
})

const fetchTopCategoriesSuccess = payload => ({
  type: FETCH_TOP_CATEGORIES_SUCCESS,
  payload,
})

const fetchTopCategoriesFailure = error => ({
  type: FETCH_TOP_CATEGORIES_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadTopCategories = () => (dispatch, getState) => {
  dispatch(fetchTopCategoriesRequest())
  return api
    .getTopLevelCategories()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const categories = []
          response.categories.forEach(category => {
            const processed = {
              id: category.id,
              name: category.name,
              img: category.img.medium,
            }
            categories.push(processed)
          })
          return dispatch(fetchTopCategoriesSuccess(categories))
        } else {
          return dispatch(fetchTopCategoriesFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchTopCategoriesFailure(
            getP(getState()).t('tv_video_library.top_level_categories_loading_error', {
              _: 'An error occurred while loading the list of top level video categories',
            })
          )
        )
    )
}

/**
 * Actions related to navigation
 */
export const SET_FOCUSED_SECTION = 'VIDEO_LIBRARY/LIST/SET_FOCUSED_SECTION'
export const SET_FOCUSED_POSITION = 'VIDEO_LIBRARY/LIST/SET_FOCUSED_POSITION'

export const setFocusedSection = focusedSection => ({
  type: SET_FOCUSED_SECTION,
  payload: focusedSection,
})
export const setFocusedPosition = (status, position) => ({
  type: SET_FOCUSED_POSITION,
  payload: {
    status,
    position,
  },
})

/**
 * Actions for fetching more videos per category from API
 */
export const FETCH_MORE_VIDEOS_REQUEST = 'VIDEO_LIBRARY/FETCH/MORE_VIDEOS/REQUEST'
export const FETCH_MORE_VIDEOS_SUCCESS = 'VIDEO_LIBRARY/FETCH/MORE_VIDEOS/SUCCESS'
export const FETCH_MORE_VIDEOS_FAILURE = 'VIDEO_LIBRARY/FETCH/MORE_VIDEOS/FAILURE'

const fetchMoreVideosRequest = categoryId => ({
  type: FETCH_MORE_VIDEOS_REQUEST,
  payload: {
    categoryId,
  },
})

const fetchMoreVideosSuccess = (categoryId, videos) => ({
  type: FETCH_MORE_VIDEOS_SUCCESS,
  payload: {
    categoryId,
    videos,
  },
})

const fetchMoreVideosFailure = (categoryId, error) => ({
  type: FETCH_MORE_VIDEOS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    categoryId,
  },
  error: true,
})

export const loadMoreVideos = categoryId => (dispatch, getState) => {
  dispatch(fetchMoreVideosRequest())
  return api
    .getCategoryVideos(categoryId, 0, 12)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const moreVideos = []
          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            moreVideos.push(processed)
          })
          return dispatch(fetchMoreVideosSuccess(categoryId, moreVideos))
        } else {
          return dispatch(fetchMoreVideosFailure(categoryId, response.reason))
        }
      },
      () =>
        dispatch(
          fetchMoreVideosFailure(
            categoryId,
            getP(getState()).t('tv_video_library.category_videos_loading_error', {
              _: 'An error occurred while loading videos per category',
            })
          )
        )
    )
}

// separate VOD page load more
export const VOD_FETCH_VIDEOS_REQUEST =
  'VIDEO_LIBRARY_SEPARATE_PAGE/FETCH/MORE_VIDEOS/REQUEST'
export const VOD_FETCH_VIDEOS_SUCCESS =
  'VIDEO_LIBRARY_SEPARATE_PAGE/FETCH/MORE_VIDEOS/SUCCESS'
export const VOD_FETCH_VIDEOS_FAILURE =
  'VIDEO_LIBRARY_SEPARATE_PAGE/FETCH/MORE_VIDEOS/FAILURE'
export const VOD_INITIAL = 'VIDEO_LIBRARY_SEPARATE_PAGE/INITIAL'

const vodFetchMoreVideosRequest = () => ({
  type: VOD_FETCH_VIDEOS_REQUEST,
  loading: true,
})

const vodFetchMoreVideosSuccess = videos => ({
  type: VOD_FETCH_VIDEOS_SUCCESS,
  videos,
  loading: false,
  hasMore: videos.length ? true : false,
})

const vodFetchMoreVideosFailure = error => ({
  type: VOD_FETCH_VIDEOS_FAILURE,
  loading: false,
})

export const vodMoreVideos = (categoryId, offset = 0) => (dispatch, getState) => {
  dispatch(vodFetchMoreVideosRequest())
  return api
    .getCategoryVideos(categoryId, offset, 999)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const moreVideos = []

          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            moreVideos.push(processed)
          })
          return dispatch(vodFetchMoreVideosSuccess(moreVideos))
        } else {
          return dispatch(vodFetchMoreVideosFailure(response.reason))
        }
      },
      () =>
        dispatch(
          vodFetchMoreVideosFailure(
            getP(getState()).t('tv_video_library.vod_videos_loading_error', {
              _: 'An error occurred while loading videos',
            })
          )
        )
    )
}

export const vodInitialState = () => dispatch => {
  dispatch({
    type: VOD_INITIAL,
  })
}
